import {
  CameraOutlined,
  DeleteOutlined,
  UploadOutlined
} from "@ant-design/icons"
import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  Steps,
  Switch,
  Typography,
  Upload,
  type UploadFile,
  type UploadProps
} from "antd"
import { type UploadChangeParam } from "antd/lib/upload"
import dayjs, { type Dayjs } from "dayjs"
import {
  type Rule,
  type RuleObject,
  type ValidatorRule
} from "rc-field-form/lib/interface"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { getUserTimezone } from "../../constants/timezones"
import {
  COMPLIANCE_DOCUMENT_EXTENSIONS,
  COMPLIANCE_DOCUMENT_MAX_MB
} from "../../containers/SubcontractorsPage/constants/compliance.ts"
import { validateFile } from "../../utils/file-validation"
import utils, { getBase64 } from "../../utils/utils"
import { passwordPolicyErrors } from "../../utils/validation/password-policy"
import { validatePhone } from "../../utils/validation/validate-phone"
import { validateZipCode } from "../../utils/validation/zip-code"
import ButtonComponent from "../Button"
import { CustomDatePicker } from "../Date&TimePickers/CustomDatePicker"
import InputNumber from "../Input/InputNumber"
import PasswordValidator from "../PasswordValidator"
import { PhoneInput } from "../PhoneInputs/PhoneInput"
import SelectComponent from "../Select"
import { TimezoneSelect } from "../Shared/TimezoneSelect"
import {
  buildSignUpSteps,
  findFirstErrorStepKey,
  hasEditableFields
} from "./sign-up-steps.ts"

const { Text, Title } = Typography

// Documents an invited profile can carry (W-9, insurance, license): PDFs and
// images. The avatar keeps its own, stricter limits above.
const DATE_FORMAT = "YYYY-MM-DD"

const MAX_SIZE_MB = 2
const ALLOWED_FILE_EXTENSIONS = ["jpeg", "jpg", "png"]

// The confirm input is posted as password_confirmation, so a rejection keyed
// to the wire name has to be translated back to reach the field.
const SERVER_FIELD_ALIASES: Record<string, string> = {
  password_confirmation: "confirm"
}

const serverFieldAlias = (name: string) => SERVER_FIELD_ALIASES[name] ?? name

const OWN_FIELD_NAMES = ["password", "confirm", "agree_terms"]

// The server sends no timezone default: its model default is UTC for everyone,
// so echoing it would overwrite the zone the browser already knows.
const resolveTimezoneDefault = (field: InviteField) =>
  field.defaultValue || getUserTimezone()

type FormErrorText = string | string[]
type SignUpFormData = Record<string, unknown>
type InviteFieldValue = string | null

type FieldChangeEvent = {
  target: {
    name: string
    value: InviteFieldValue
  }
}

// Shown only while another field holds (or does not hold) one of these values:
// the EIN/SSN swap by entity type, the ACH numbers by payment method.
interface FieldVisibility {
  field: string
  in?: string[]
  notIn?: string[]
}

interface InviteField {
  name: string
  type:
    | "input"
    | "input_number"
    | "select"
    | "switch"
    | "timezone"
    | "section"
    | "date"
    | "file"
  label: string
  hint?: string | null
  accept?: string
  maxSizeMb?: number
  // A document is already on file (entered by whoever sent the invitation).
  onFile?: boolean
  visibleWhen?: FieldVisibility
  placeholder?: string
  required?: boolean
  message?: string
  validator?: ValidatorRule["validator"]
  disabled?: boolean
  defaultValue?: InviteFieldValue | string[] | number
  multiple?: boolean
  mode?: React.ComponentProps<typeof SelectComponent>["mode"]
  options?: React.ComponentProps<typeof SelectComponent>["options"]
}

interface InviteInfo {
  fields?: InviteField[]
  // The address the invitation was sent to. Shown, never submitted: the server
  // takes it from the invite code, and it is not one of the served fields.
  email?: string
  name?: string
}

interface AvatarState {
  file: File
  src: string
}

interface SignUpEmailFormProps {
  error?: string | Record<string, FormErrorText> | null
  initialValues?: SignUpFormData | null
  inviteInfo?: InviteInfo | null
  isInvite: boolean
  onSubmit: (data: SignUpFormData) => void
  children?: React.ReactNode
}

const isHiddenBy = (
  field: InviteField,
  values: Record<string, unknown>
): boolean => {
  const rule = field.visibleWhen
  if (!rule) return false

  const current = values[rule.field] as string | undefined

  if (rule.in && !(current !== undefined && rule.in.includes(current))) {
    return true
  }
  if (rule.notIn && current !== undefined && rule.notIn.includes(current)) {
    return true
  }
  return false
}

function SignUpEmailForm({
  error,
  initialValues,
  inviteInfo,
  isInvite,
  ...props
}: SignUpEmailFormProps) {
  const [errorText, setErrorText] = useState<FormErrorText | null>(null)
  const [value, setValue] = useState<InviteFieldValue>(null)
  const [avatar, setAvatar] = useState<AvatarState | null>(null)

  const [isVisibleBtn, setIsVisibleBtn] = useState(false)
  const [isBackError, setIsBackError] = useState(false)

  const [form] = Form.useForm()
  const { t } = useTranslation()

  // A subcontractor's invitation carries the whole profile, which the design
  // review found unusable as one scroll. Anything shorter — every internal role
  // — comes back as the Account step alone and renders exactly as it did.
  // Account survives whether or not the invitation put a field on it: the
  // password, its confirmation and the terms live there and are never served.
  const steps = useMemo(
    () =>
      buildSignUpSteps(inviteInfo?.fields ?? []).filter(
        (step) => step.key === "account" || hasEditableFields(step)
      ),
    [inviteInfo]
  )
  const isWizard = steps.length > 1
  const [stepKey, setStepKey] = useState(steps[0]?.key ?? "account")
  const stepIndex = Math.max(
    steps.findIndex((step) => step.key === stepKey),
    0
  )
  const isLastStep = stepIndex === steps.length - 1

  // A second invitation can arrive without this component unmounting — the
  // route is the same and only `?invite=` changes. Both invitations are
  // subcontractors' often enough that the step keys match, so nothing would
  // reset the position: the new invitee would land mid-wizard, never see the
  // Account step or the address their invitation was sent to, and could reach
  // the final button without passing the steps in between.
  const invitedEmail = inviteInfo?.email
  useEffect(() => {
    setStepKey("account")
  }, [invitedEmail])

  const formFieldNames = useMemo(
    () =>
      new Set([
        ...OWN_FIELD_NAMES,
        ...(inviteInfo?.fields ?? []).map((field) => field.name)
      ]),
    [inviteInfo]
  )

  // Selecting an option has to clear a server rejection on that field too,
  // or the message outlives the value the user just corrected.
  const onFieldChange = (field: InviteField) => (value: unknown) => {
    handleChange({
      target: { name: field.name, value: value as InviteFieldValue }
    })
    handleClearError(field.name)
  }

  const getFieldRules = (field: InviteField): Rule[] => {
    const variants: Partial<Record<string, ValidatorRule["validator"]>> = {
      phone: validatePhone,
      zip_code: validateZipCode
    }

    const validator = variants?.[field?.name] || field?.validator || null

    const rules: Rule[] = [
      {
        required: field.required,
        ...(validator ? { validator } : { message: field.message })
      },
      ...(extraRules?.[field.name] || [])
    ]

    return rules
  }

  // A field-keyed rejection belongs on that field, where the user is looking.
  // Only errors with nowhere to land fall through to the banner. Depending on
  // `error` itself, not on whether one exists, so a second rejection with
  // different text replaces the first instead of leaving the stale one up.
  useEffect(() => {
    if (!error) return

    if (typeof error === "string") {
      setErrorText(
        error.length > 100
          ? t("Something went wrong, please try later again")
          : error
      )
      setIsBackError(true)
      return
    }

    const fieldErrors = Object.entries(error).filter(([name]) =>
      formFieldNames.has(serverFieldAlias(name))
    )

    if (fieldErrors.length) {
      form.setFields(
        fieldErrors.map(([name, messages]) => ({
          name: serverFieldAlias(name),
          errors: (Array.isArray(messages) ? messages : [messages]).map(String)
        }))
      )
      setErrorText(null)
      setIsBackError(false)
      return
    }

    setErrorText(Object.values(error)[0])
    setIsBackError(true)
  }, [error])

  useEffect(() => {
    if (inviteInfo) {
      const fields = inviteInfo?.fields

      if (fields && fields.length > 0) {
        const defaultValues: SignUpFormData = {}
        fields.forEach((field) => {
          if (field?.type === "section" || field?.type === "file") {
            // Nothing to prefill: a section is a heading, and a document
            // already on file is shown as a note, never as a value.
            return
          } else if (field?.type === "date") {
            defaultValues[field.name] = field.defaultValue
              ? dayjs(String(field.defaultValue))
              : undefined
          } else if (field?.type === "timezone") {
            defaultValues[field.name] = resolveTimezoneDefault(field)
          } else if (field?.type === "select" && !field?.multiple) {
            defaultValues[field.name] = field.defaultValue || undefined
          } else {
            defaultValues[field.name] = field.defaultValue || ""
          }
        })
        form.setFieldsValue({ ...defaultValues })
      }
    }
  }, [inviteInfo])

  const onFinish = (values: SignUpFormData) => {
    const { confirm, ...data } = values

    // Dates go out as plain strings, documents as the File itself, and a field
    // hidden by its condition (say the EIN once the entity is a sole proprietor)
    // is not sent at all, even if something was typed before switching.
    ;(inviteInfo?.fields ?? []).forEach((field) => {
      if (isHiddenBy(field, values)) {
        delete data[field.name]
        return
      }
      if (field.type === "date") {
        const value = data[field.name] as Dayjs | undefined
        data[field.name] =
          value && typeof value.format === "function"
            ? value.format(DATE_FORMAT)
            : undefined
      }
      if (field.type === "file") {
        const fileList = data[field.name] as UploadFile[] | undefined
        data[field.name] = fileList?.[0]?.originFileObj as File | undefined
      }
    })

    const formData = {
      ...data,
      password_confirmation: confirm,
      ...(avatar && { avatar: avatar.file })
    }

    props.onSubmit(formData)
  }

  const onFinishFailed = ({
    errorFields
  }: {
    errorFields?: Array<{ name: Array<string | number>; errors: string[] }>
  }) => {
    if (!errorFields?.length) return

    setErrorText(errorFields[0]["errors"])

    // A refused save used to leave the user wherever they were reading, with
    // the field it was refused over on another step and out of sight.
    const key = findFirstErrorStepKey(
      steps,
      errorFields.map(({ name }) => String(name[0]))
    )
    if (key && key !== stepKey) setStepKey(key)
  }

  function handleClearError(field: string) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    let newError: FormErrorText | null = null
    const errorList = form.getFieldsError()

    errorList.forEach((field) => {
      if (field.errors.length && !newError) newError = field.errors[0]
    })
    setIsBackError(false)
    setErrorText(newError)
  }

  function handleChange(e: FieldChangeEvent) {
    const { name, value } = e.target

    if (name === "password") {
      setValue(value)
    }

    // Gate on the same policy the server enforces; the old length-6 threshold
    // enabled submit for passwords the API would refuse.
    const password = (
      name === "password" ? value : form.getFieldValue("password")
    ) as string

    const emailAccepted =
      isInvite ||
      utils.validateEmail(
        (name === "email" ? value : form.getFieldValue("email")) as string
      )

    setIsVisibleBtn(
      Boolean(
        emailAccepted && password && !passwordPolicyErrors(password).length
      )
    )
  }

  const normFile = (e: UploadChangeParam<UploadFile> | UploadFile[]) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList
  }

  const handleAvatarChange = (info: UploadChangeParam<UploadFile>) => {
    const { file } = info
    if (file.status === "done") {
      getBase64(file.originFileObj, (imageUrl: string) => {
        setAvatar({ file: file.originFileObj!, src: imageUrl })
      })
    }
  }

  const handleDeleteAvatar = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAvatar(null)
    form.setFieldsValue({ avatar: null })
  }

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    return validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS,
      defaultResult: true
    })
  }

  const fakeRequest: UploadProps["customRequest"] = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess!("ok")
    }, 500)
  }

  const uploadProps: UploadProps = {
    name: "avatar",
    listType: "picture-circle",
    showUploadList: false,
    multiple: false,
    accept: ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    maxCount: 1,
    customRequest: fakeRequest,
    beforeUpload: beforeUpload,
    onChange: handleAvatarChange
  }

  const extraRules: Record<string, Rule[]> = {
    name: [
      {
        type: "string",
        message: t("You can only use letters to insert your name"),
        pattern: /^[a-zA-Z ]+$/,
        transform(value: string) {
          if (value) return value.trim()
        }
      }
    ],
    email: [
      {
        type: "email",
        message: t("The email you entered is incorrect")
      }
    ]
  }

  // An array of messages concatenated with `+` renders as comma-joined junk.
  const bannerText = Array.isArray(errorText)
    ? errorText.join(" ")
    : (errorText ?? "")

  // "Kindly try another one or Sign In" only makes sense when the account is
  // the problem; it read as a non-sequitur under a weak-password message.
  const suggestsExistingAccount =
    /social|already|exists|registered|taken/i.test(bannerText)

  const renderField = (field: InviteField) => {
    switch (field.type) {
      case "input": {
        const Comp = field?.name === "phone" ? PhoneInput : Input

        return (
          <Form.Item
            key={field.name}
            label={field.label}
            name={field.name}
            // validateStatus={
            //   field.name === "email" && isBackError ? "error" : null
            // }
            rules={getFieldRules(field)}
          >
            <Comp
              placeholder={field.placeholder || field.label}
              onChange={(e) => {
                handleChange(e)
                handleClearError(field.name)
              }}
              disabled={field.disabled}
            />
          </Form.Item>
        )
      }

      case "input_number":
        return (
          <Form.Item
            key={field.name}
            label={field.label}
            name={field.name}
            rules={[
              {
                required: field.required,
                message: field?.message
              }
            ]}
          >
            <InputNumber
              prefix="$"
              style={{ width: "100%" }}
              placeholder={field.placeholder || field.label}
              min={0}
              max={999999999.99}
              precision={2}
              disabled={field.disabled}
            />
          </Form.Item>
        )
      case "select":
        return (
          <Form.Item
            key={field.name}
            label={field.label}
            name={field.name}
            rules={[
              {
                required: field.required,
                message: field?.message
              }
            ]}
          >
            <SelectComponent
              mode={field?.mode ?? undefined}
              options={field?.options || []}
              placeholder={field.placeholder || field.label}
              onChange={onFieldChange(field)}
              disabled={field.disabled}
              {...(field?.mode === "multiple"
                ? {
                    filterOption: (input, option) =>
                      ((option?.label ?? "") as string)
                        .toLowerCase()
                        .includes(input?.toLowerCase())
                  }
                : {})}
            />
          </Form.Item>
        )
      case "timezone":
        return (
          <Form.Item
            key={field.name}
            label={field.label}
            name={field.name}
            rules={[
              {
                required: field.required,
                message: field?.message
              }
            ]}
          >
            <TimezoneSelect
              placeholder={field.placeholder || field.label}
              disabled={field.disabled}
              onChange={onFieldChange(field)}
            />
          </Form.Item>
        )
      case "switch":
        return (
          <Form.Item
            key={field.name}
            label={field.label}
            name={field.name}
            valuePropName="checked"
            style={{ display: "inline-block", width: "50%" }}
          >
            <Switch />
          </Form.Item>
        )
      case "section":
        return (
          <div key={field.name} className="pt-2 pb-2">
            <Title level={5} className="!mb-1">
              {field.label}
            </Title>
            {field.hint && <Text type="secondary">{field.hint}</Text>}
          </div>
        )
      case "date":
        return (
          <Form.Item key={field.name} label={field.label} name={field.name}>
            <CustomDatePicker
              className="w-full"
              format={DATE_FORMAT}
              placeholder={field.placeholder || DATE_FORMAT}
              disabled={field.disabled}
            />
          </Form.Item>
        )
      case "file":
        return (
          <Form.Item
            key={field.name}
            label={field.label}
            name={field.name}
            valuePropName="fileList"
            getValueFromEvent={normFile}
            extra={
              field.onFile
                ? t(
                    "A document is already on file. Uploading a new one replaces it."
                  )
                : undefined
            }
          >
            <Upload
              accept={field.accept}
              maxCount={1}
              disabled={field.disabled}
              beforeUpload={(file) =>
                validateFile(file, {
                  maxSize: field.maxSizeMb ?? COMPLIANCE_DOCUMENT_MAX_MB,
                  allowedExtensions: COMPLIANCE_DOCUMENT_EXTENSIONS,
                  defaultResult: false
                })
              }
            >
              <Button icon={<UploadOutlined />}>{t("Select file")}</Button>
            </Upload>
          </Form.Item>
        )
    }
  }

  // One step's fields, in the order that step declares rather than the order
  // the invitation listed them.
  const renderFields = (fields: InviteField[]) =>
    fields.map((field) =>
      field.visibleWhen ? (
        <Form.Item noStyle shouldUpdate key={field.name}>
          {({ getFieldsValue }) =>
            isHiddenBy(field, getFieldsValue(true)) ? null : renderField(field)
          }
        </Form.Item>
      ) : (
        renderField(field)
      )
    )

  const submitButton = (
    <Button
      type="primary"
      htmlType="submit"
      // Across a wizard the password is several steps back, so a button
      // disabled with nothing on screen to explain it is a dead end. Let the
      // save be refused and routed to the field, as the document pages do.
      disabled={isWizard ? false : !isVisibleBtn}
      block
      className={"mt-0"}
    >
      {t("Create an account")}
    </Button>
  )

  if (!isInvite) return null

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger={["onSubmit", "onChange"]}
      className="login-form with-default-error"
      layout="vertical"
      size="large"
      initialValues={{
        portal_notifications: true,
        email_notifications: true,
        ...initialValues
      }}
    >
      {props.children}

      {isWizard && (
        <Steps
          size="small"
          className="mb-6"
          current={stepIndex}
          onChange={(next) => setStepKey(steps[next].key)}
          items={steps.map((step) => ({
            key: step.key,
            title: t(step.label)
          }))}
        />
      )}

      {/* Outside the panels: a rejection with no field to land on is
          raised here, and the save that caused it is made on the last
          step — inside the Account panel it would be invisible exactly
          when it is the only thing the user has to go on. */}
      <Form.Item noStyle shouldUpdate>
        {() => (
          <div className="error-text">
            {isBackError &&
              (suggestsExistingAccount ? (
                <>
                  {`${bannerText} ${t("Kindly try another one or")} `}
                  <Link to="/login">{t("Sign In")}</Link>
                </>
              ) : (
                bannerText
              ))}
          </div>
        )}
      </Form.Item>

      {/* Every step stays mounted: one submit has to validate all of them,
          and a value typed two steps ago must not be lost by looking away. */}
      <div className={isWizard && stepIndex !== 0 ? "hidden" : undefined}>
        {isWizard && inviteInfo?.email && (
          <Flex vertical gap={4} className="mb-4">
            <Text type="secondary">{t("Invited email")}</Text>
            <Input value={inviteInfo.email} readOnly disabled />
          </Flex>
        )}

      <Form.Item
        name="avatar"
        className="text-center"
        style={{ marginBottom: 0 }}
        getValueFromEvent={normFile}
      >
        <div className="relative inline-block group">
          <Upload {...uploadProps}>
            {avatar ? (
              <img
                src={avatar?.src}
                alt="avatar"
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <Flex gap={4} vertical align="center">
                <CameraOutlined />
                <Text>Avatar</Text>
              </Flex>
            )}
          </Upload>
          {!!avatar && (
            <ButtonComponent
              shape="circle"
              size="small"
              onClick={handleDeleteAvatar}
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
              icon={<DeleteOutlined />}
            />
          )}
        </div>
      </Form.Item>

      {renderFields(steps[0]?.fields ?? [])}

      <Form.Item
        label="Password"
        name="password"
        rules={[
          { required: true, message: t("Please insert your password") },
          {
            validator: (_rule: RuleObject, value: string) => {
              const missing = passwordPolicyErrors(value)

              return missing.length
                ? Promise.reject(
                    new Error(
                      `${t("Password must contain")} ${missing
                        .map((requirement) => t(requirement))
                        .join(", ")}.`
                    )
                  )
                : Promise.resolve()
            }
          }
        ]}
      >
        <Input.Password
          placeholder={t("Password")}
          name={"password"}
          type={"password"}
          autoComplete="new-password"
          visibilityToggle
          onChange={(e) => {
            handleChange(e)
            handleClearError("password")
          }}
        />
      </Form.Item>

      {/* Errors reach their own field now, so this stays a strength meter and
          keeps its guidance on screen exactly when the password was refused. */}
      <PasswordValidator value={value} error={null} isLogin={true} />

      <Form.Item
        name="confirm"
        rules={[
          {
            required: true,
            message: t("Please confirm your password!")
          },
          ({ getFieldValue }) => ({
            // Confirm only answers "do these match". Length and composition
            // are the password field's own business, so a weak password is
            // reported where it was typed instead of one field lower.
            validator(_rule: RuleObject, value: string) {
              if (
                !value ||
                !getFieldValue("password") ||
                getFieldValue("password") === value
              ) {
                return Promise.resolve()
              }

              return Promise.reject(new Error(t("Passwords do not match")))
            }
          })
        ]}
      >
        <Input.Password
          name="confirm"
          placeholder={t("Confirm password")}
          autoComplete="dontshow"
          visibilityToggle
          onChange={(e) => {
            handleChange(e)
            handleClearError("confirm")
          }}
        />
      </Form.Item>

      <Form.Item
        name="agree_terms"
        valuePropName="checked"
        rules={[
          {
            validator: (_: RuleObject, value: boolean) =>
              value
                ? Promise.resolve()
                : Promise.reject(t("You must agree to the terms"))
          }
        ]}
      >
        <Checkbox>
          {t("I have read and agree to the")}{" "}
          <Typography.Link
            href={import.meta.env.VITE_APP_PRIVACY_POLICY}
            target="_blank"
          >
            {t("Privacy Policy")}
          </Typography.Link>{" "}
          {t("and")}{" "}
          <Typography.Link
            href={import.meta.env.VITE_APP_TERMS_AND_CONDITIONS}
            target="_blank"
          >
            {t("Terms and Conditions")}
          </Typography.Link>
        </Checkbox>
      </Form.Item>
      </div>

      {steps.slice(1).map((step, index) => (
        <div
          key={step.key}
          className={stepIndex === index + 1 ? undefined : "hidden"}
        >
          {renderFields(step.fields)}
        </div>
      ))}

      {isWizard ? (
        <Flex gap="middle" className="mt-8">
          {stepIndex > 0 && (
            <Button block onClick={() => setStepKey(steps[stepIndex - 1].key)}>
              {t("Back")}
            </Button>
          )}
          {isLastStep ? (
            submitButton
          ) : (
            <Button
              type="primary"
              block
              onClick={() => setStepKey(steps[stepIndex + 1].key)}
            >
              {t("Continue")}
            </Button>
          )}
        </Flex>
      ) : (
        submitButton
      )}
    </Form>
  )
}

export default SignUpEmailForm
